import { AxiosError, AxiosResponse } from 'axios';
import { DropDownData, DropDownTreeData } from '../types/dropdown.api';
import {
   CategoryListItem,
   DeleteEmissionFactorYearRequest,
   EmissionFactorsApiResponse,
   EmissionFactorGroupsResponse,
   UpdateEmissionFactorRequest,
   UpdateEmissionFactorResponse,
} from '../features/emission-factor/types/emission-factor.api';
import { ItemResponse, ModifyResponse, RefreshResponse } from '../features/items/types/items.api';
import {
   PiaMaterialMappingData,
   PiaMaterialMappingRequest,
   PiaMaterialMappingResponse,
} from '../features/material-grade/types/material-mapping.api';
import { convertBackendItemToFrontendItem } from '../types/item.mapper';

import { DEFAULT_QUERY } from '../features/items/state/table-data.state';
import { Item } from '../features/items/types/item.types';
import { Query } from '../shared/types/query.types';
import { SubItem } from '../features/items/types/sub-item.types';
import { SuggestedMaterialGradesResponse } from '../features/material-grade/hooks/useMaterialGradeSuggestions';
import http from '../http-common';
import { omit } from 'lodash';
import { parseNumericValue } from '../shared/utils/formatters.utils';
import { convertFrontItemToQuery } from '../types/query.mapper';
import { useActionState } from '../features/status/state/actions.state';
import { ExistingMode } from '../features/items/components/copy-paste/types/copy-paste.types';

const RETRY_COUNT = 3;
const INITIAL_RETRY_DELAY = 1000;

export enum ModifyItemsScope {
   acknowledgePiaUpdates = 'acknowledgePiaUpdates',
   applicableNetWeights = 'applicableNetWeights',
}

const addBomValidFrom = <T extends Record<string, any>>(payload: T, bomValidFrom: string | null): T => {
   return bomValidFrom ? { ...payload, bomValidFrom } : payload;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetry = (error: AxiosError): boolean => {
   return !error.response || error.response.status >= 500;
};

async function withRetry<T>(operation: () => Promise<T>, retries: number = RETRY_COUNT, delay: number = INITIAL_RETRY_DELAY): Promise<T> {
   try {
      return await operation();
   } catch (error) {
      const axiosError = error as AxiosError;
      if (retries > 0 && shouldRetry(axiosError)) {
         console.log(`Retrying... ${retries} retries left`);
         await wait(delay);
         return withRetry(operation, retries - 1, delay * 2);
      } else {
         throw error;
      }
   }
}

const getItems = (query: Query): Promise<ItemResponse> => {
   const queryWithBom = addBomValidFrom(convertFrontItemToQuery(query), query.bomValidFrom || null);
   return withRetry(() => http.post<ItemResponse>('/Filter', queryWithBom).then((res: AxiosResponse<ItemResponse>) => res.data));
};

const getItemsForExport = (query: Query): Promise<ItemResponse> => {
   const exportQuery = addBomValidFrom({ ...convertFrontItemToQuery(query), addMaterial: true }, query.bomValidFrom || null);
   return withRetry(() => http.post<ItemResponse>('/Filter', exportQuery).then((res: AxiosResponse<ItemResponse>) => res.data));
};

const refreshItems = (items: Item[], reset: boolean = false): Promise<RefreshResponse> => {
   const itemNoArr: string[] = items.map((item) => item.itemNo.toString());
   const payload = { itemNos: itemNoArr, reset: reset };
   return withRetry(() => http.post<RefreshResponse>('/RefreshItems', payload).then((res: AxiosResponse<RefreshResponse>) => res.data));
};

const modifyOrCreateSubItems = (changed: Array<Record<string, any>> | Record<string, any>): Promise<ModifyResponse> => {
   const operation = async () => {
      const actionState = useActionState.getState();
      const item = actionState.editRowData;
      const parentItem = actionState.parentItem;

      const bomValidFrom = parentItem?.isVersionedItem === true && parentItem?.bomValidFrom ? parentItem.bomValidFrom : null;

      const suppliers = item?.suppliers || [];
      const singleChangedItem = !Array.isArray(changed) ? changed : changed.length === 1 ? changed[0] : undefined;

      const supplierMaterial = [changed].flat().map((subItem) => {
         const filteredProps = omit(subItem, 'dwpItemMatSet', 'parts', 'subItems', ...(suppliers.map((sd: any) => sd.buCodeSup) || []));
         return addBomValidFrom({ ...filteredProps, id: subItem.id ?? -1 }, bomValidFrom);
      });

      const dwpItemMatSet = suppliers
         .filter((supplier: any) => singleChangedItem?.[supplier.buCodeSup] !== undefined)
         .map((supplier: any) => {
            const basePayload = {
               parentId: item!.parentId,
               matSetName: item!.matSetName,
               id: item!.id,
               included: supplier.included,
               buCodeSup: supplier.buCodeSup,
            };
            return addBomValidFrom(basePayload, bomValidFrom);
         });

      const res = await http.post<ModifyResponse>('/Modify', { supplierMaterial, dwpItemMatSet });
      return res.data;
   };

   return withRetry(operation);
};

const acknowledgePiaItemUpdates = (changed: Partial<Item> | Partial<Item>[]): Promise<ModifyResponse> => {
   const sourceItems = Array.isArray(changed) ? changed : [changed];
   const payloadArr = sourceItems.map((sourceItem) => ({
      itemNo: sourceItem.itemNo,
      newFromPia: false,
      updatedByPia: false,
   }));

   return withRetry(async () => {
      const response = await http.post<ModifyResponse>('/Modify', { items: payloadArr });
      return response.data;
   });
};

const setApplicableItemNetWeights = (changed: Partial<Item> | Partial<Item>[]): Promise<ModifyResponse> => {
   const parentItem = useActionState.getState().parentItem;

   const itemChanges = [changed]
      .flat()
      .filter((it) => it.dwpWarningEnabled !== parentItem?.dwpWarningEnabled)
      .map((it) => ({ itemNo: it.itemNo, dwpWarningEnabled: it.dwpWarningEnabled }));

   return withRetry(async () => {
      const response = await http.post<ModifyResponse>('/Modify', {
         items: itemChanges.length > 0 ? itemChanges : undefined,
      });
      return response.data;
   });
};

const setAllowDwpDeviationWarning = (item: Partial<Item> | Partial<Item>[], dwpWarningEnabled: boolean): Promise<ModifyResponse> => {
   const items = [item].flat();
   return withRetry<ModifyResponse>(async () => {
      const response = await http.post<ModifyResponse>('/Modify', {
         items: items.map((item) => ({ itemNo: item.itemNo, dwpWarningEnabled })),
      });
      return response.data;
   });
};

const copyParts = async (payload: {
   itemsFrom: Array<{ itemNo: string; materialSetName: string; id: number }>;
   itemNos: string[];
   pasteTypes: string[];
   existingMode: ExistingMode;
   createMissing: boolean;
}): Promise<ModifyResponse> => {
   const res = await http.post<ModifyResponse>('/Modify', { supplierMaterialCopyPaste: payload });
   return res.data;
};

const getDropDownData = (): Promise<DropDownData> => {
   return withRetry<DropDownData>(async () => {
      const response = await http.get<DropDownData>('/DropdownData');
      return response.data;
   });
};

const getDropDownTreeData = (): Promise<DropDownTreeData> => {
   return withRetry<DropDownTreeData>(async () => {
      const response = await http.get<DropDownTreeData>('/DropdownTreeData');
      return response.data;
   });
};

const deleteSubItems = (subItems: SubItem[]): Promise<void> => {
   return withRetry<void>(async () => {
      await http.post('/Delete', {
         supplierMaterial: subItems.map((s) =>
            addBomValidFrom({ parentId: s.parentId, id: s.id, matSetName: s.matSetName }, s.bomValidFrom || null)
         ),
      });
      return undefined;
   });
};

const getBaseAndBasedOn = (itemNo: string): Promise<string[]> => {
   return withRetry<string[]>(async () => {
      const response = await http.get<string[]>(`/BaseAndBasedOn?itemNo=${itemNo}`);
      return response.data;
   });
};

const updateNewFromPiaStatus = (subItem: SubItem): Promise<void> => {
   const payload = {
      supplierMaterial: [
         {
            parentId: subItem.parentId,
            matSetName: subItem.matSetName,
            id: subItem.id,
            part: subItem.part,
            materialGrade: subItem.materialGrade,
            partDetail: subItem.partDetail,
            threeDModelNo: subItem.threeDModelNo,
            active: subItem.active,
            locked: subItem.locked,
            clientCreated: subItem.clientCreated,
            newFromPia: false,
            comment: subItem.comment,
            volume: subItem.volume,
            mass: subItem.mass,
            quantity: subItem.quantity,
            materialGradeId: parseInt(subItem.materialGradeId, 10),
            density: subItem.density,
            recycledContent: subItem.recycledContent,
            renewableContent: subItem.renewableContent,
            compositionShare: subItem.compositionShare || 0,
         },
      ],
   };

   return withRetry<void>(async () => {
      await http.post('/Modify', payload);
      return undefined;
   });
};

const cloneBom = (itemNo: string, bomValidFrom: string): Promise<boolean> => {
   return withRetry<boolean>(async () => {
      try {
         await http.post('/Clone', { itemNo, bomValidFrom });
         return true;
      } catch (error) {
         console.error('Error cloning BOM:', error);
         return false;
      }
   });
};

const getBomValidFromDates = (itemNo: string): Promise<string[]> => {
   return withRetry<string[]>(async () => {
      try {
         const response = await http.get<string[]>(`/BomValidFrom?itemNo=${itemNo}`);
         return response.data || [];
      } catch (error) {
         console.error('Error getting BOM history:', error);
         return [];
      }
   });
};

const getBomVersion = (itemNo: string, bomValidFrom: string): Promise<Item | null> => {
   return withRetry<Item | null>(async () => {
      try {
         const filterQuery: Query = {
            ...DEFAULT_QUERY,
            itemNos: [itemNo],
            bomValidFrom: bomValidFrom,
            pageSize: 1,
            offset: 0,
            progress: false,
         };

         const response = await http.post<ItemResponse>('/Filter', filterQuery);

         if (!response.data.items || response.data.items.length === 0) {
            return null;
         }

         return convertBackendItemToFrontendItem(response.data.items[0]);
      } catch (error) {
         console.error('Error getting BOM version:', error);
         return null;
      }
   });
};

const getPiaMaterialMapping = (request: PiaMaterialMappingRequest): Promise<PiaMaterialMappingResponse> => {
   return withRetry<PiaMaterialMappingResponse>(async () => {
      const response = await http.post<PiaMaterialMappingResponse>('/PiaMaterialMapping', {
         itemNo: request.itemNo,
         familyName: request.familyName,
         materialPart: request.materialPart,
         materialPartText: request.materialPartText,
         doCount: request.doCount || false,
      });
      return response.data;
   });
};

const modifyPiaMaterialMapping = (piaMaterialMapping: PiaMaterialMappingData): Promise<ModifyResponse> => {
   return withRetry<ModifyResponse>(async () => {
      const response = await http.post<ModifyResponse>('/Modify/PiaMaterialMapping', piaMaterialMapping);
      return response.data;
   });
};

const getSuggestedMaterialGrades = (materialGradeId: string): Promise<SuggestedMaterialGradesResponse> => {
   return withRetry<SuggestedMaterialGradesResponse>(async () => {
      const response = await http.get<SuggestedMaterialGradesResponse>(`/SuggestedMaterialGrades?materialGradeId=${materialGradeId}`);
      return response.data;
   });
};

type TupleValue = string | number | boolean;

interface CategoryTupleResponse {
   item1?: string;
   item2?: string;
   item3?: TupleValue;
   item4?: TupleValue;
}

interface NormalizedTuple {
   id: string;
   name: string;
   rawCurrent: TupleValue | undefined;
   rawTotal: TupleValue | undefined;
}

const NO_SUB_ID_FALLBACK = '<No ID>';

const isNoSubTypeId = (subTypeId: string): boolean => subTypeId.trim().toLowerCase() === NO_SUB_ID_FALLBACK.toLowerCase();

const normalizeTuple = (tuple: CategoryTupleResponse): NormalizedTuple => ({
   id: (tuple.item1 ?? '').trim(),
   name: (tuple.item2 ?? '').trim(),
   rawCurrent: tuple.item3,
   rawTotal: tuple.item4,
});

const hasTupleProgress = ({ rawCurrent, rawTotal }: Pick<NormalizedTuple, 'rawCurrent' | 'rawTotal'>): boolean =>
   rawCurrent != null || rawTotal != null;

interface TupleToCategoryOptions {
   allowBooleanProgress?: boolean;
}

const toCategoryListItem = (
   { id, name, rawCurrent, rawTotal }: NormalizedTuple,
   { allowBooleanProgress = false }: TupleToCategoryOptions = {}
): CategoryListItem => {
   const isBooleanProgress = allowBooleanProgress && typeof rawCurrent === 'boolean' && rawTotal == null;
   const progressCurrent = rawCurrent == null ? undefined : isBooleanProgress ? (rawCurrent ? 1 : 0) : parseNumericValue(rawCurrent);
   const progressTotal = rawTotal == null ? (isBooleanProgress ? 1 : undefined) : parseNumericValue(rawTotal);

   return { id, name: name || undefined, progressCurrent, progressTotal };
};

const mapTuples = (data: CategoryTupleResponse[], options?: TupleToCategoryOptions): CategoryListItem[] =>
   data
      .map(normalizeTuple)
      .filter((tuple) => tuple.id.length > 0 || tuple.name.length > 0 || hasTupleProgress(tuple))
      .map((tuple) =>
         tuple.id.length > 0 || tuple.name.length > 0
            ? tuple
            : {
               ...tuple,
               id: NO_SUB_ID_FALLBACK,
            }
      )
      .map((tuple) => toCategoryListItem(tuple, options));

const fetchCategoryTuples = (url: string, options?: TupleToCategoryOptions): Promise<CategoryListItem[]> =>
   withRetry(() => http.get<CategoryTupleResponse[]>(url).then((res) => mapTuples(res.data, options)));

const getEmissionFactorCategories = (): Promise<CategoryListItem[]> => fetchCategoryTuples('/EmissionFactors/Categories');

const getEmissionFactorTypes = (categoryId: string): Promise<CategoryListItem[]> =>
   fetchCategoryTuples(`/EmissionFactors/Category/${categoryId}/Types`);

const getEmissionFactorSubtypes = (categoryId: string, typeId: string): Promise<CategoryListItem[]> =>
   fetchCategoryTuples(`/EmissionFactors/Category/${categoryId}/Type/${typeId}/SubTypes`);

const getEmissionFactorGradeIds = (categoryId: string, typeId: string, subTypeId: string): Promise<CategoryListItem[]> => {
   const requestSubTypeId = isNoSubTypeId(subTypeId) ? '0' : subTypeId;
   return fetchCategoryTuples(`/EmissionFactors/Category/${categoryId}/Type/${typeId}/SubType/${requestSubTypeId}/GradeIds`, {
      allowBooleanProgress: true,
   });
};

const getEmissionFactors = (request: {
   categoryId?: string;
   materialTypeId?: string;
   subtypeId?: string;
   gradeId?: string;
}): Promise<EmissionFactorsApiResponse> =>
   withRetry(() =>
      http
         .get<EmissionFactorsApiResponse>('/EmissionFactorsInternal', {
            params: {
               categoryId: request.categoryId,
               typeId: request.materialTypeId,
               subTypeId: request.subtypeId,
               gradeId: request.gradeId,
            },
         })
         .then((res) => res.data)
   );

const getEmissionFactorGroups = (): Promise<EmissionFactorGroupsResponse> =>
   withRetry(() => http.get<EmissionFactorGroupsResponse>('/EmissionFactors/Groups').then((res) => res.data));

const updateEmissionFactors = (request: UpdateEmissionFactorRequest): Promise<UpdateEmissionFactorResponse> => {
   return withRetry(() =>
      http
         .post<UpdateEmissionFactorResponse>('/Modify/EmissionFactors', request)
         .then((res: AxiosResponse<UpdateEmissionFactorResponse>) => res.data)
   );
};

const deleteEmissionFactorYears = (gradeId: string, years: number[]): Promise<void> => {
   const payload: DeleteEmissionFactorYearRequest = { years: years.map((year) => ({ gradeId, year })) };
   return withRetry<void>(async () => {
      await http.post('/Delete/EmissionFactors/Year', payload);
      return undefined;
   });
};

const APIService = {
   getItems,
   getItemsForExport,
   setApplicableItemNetWeights,
   acknowledgePiaItemUpdates,
   modifySubItems: modifyOrCreateSubItems,
   deleteSubItems,
   getDropDownData,
   getDropDownTreeData,
   refreshItems,
   setAllowDwpDeviationWarning,
   copyParts,
   updateNewFromPiaStatus,
   getBaseAndBasedOn,
   cloneBom,
   getBomValidFromDates,
   getBomVersion,
   getPiaMaterialMapping,
   modifyPiaMaterialMapping,
   getSuggestedMaterialGrades,
   getEmissionFactorCategories,
   getEmissionFactorTypes,
   getEmissionFactorSubtypes,
   getEmissionFactorGradeIds,
   getEmissionFactors,
   getEmissionFactorGroups,
   updateEmissionFactors,
   deleteEmissionFactorYears,
};

export default APIService;
